/**
 * Tâche 1289 (Eric 2026-06-30) — Relire le courriel du lundi, sans rien envoyer.
 *
 * Le courriel est affiché TEL QUEL dans un cadre isolé (`iframe` en bac à
 * sable, `srcDoc`) : ce qu'on relit est exactement le HTML qui partira, pas
 * une reconstitution. Aucun appel au fournisseur n'est fait à l'ouverture.
 *
 * Tâche 1430 (Eric 2026-10-01) — Cette fenêtre était restée entièrement en
 * français : elle avait été écrite sans `useLang`, et l'anglais s'arrêtait
 * donc au bouton « Read email » qui l'ouvre. Deux natures de texte à
 * distinguer ici :
 *   - ce que l'INTERFACE dit (titres, boutons, constats) : traduit ;
 *   - le COURRIEL lui-même, affiché dans le cadre isolé : il reste en
 *     français, puisque c'est le message réel qui partira. Traduire l'aperçu
 *     mentirait sur ce que recevra le destinataire.
 * Les phrases d'état viennent du serveur (`empty_reason`, `blocked_by`) et ne
 * peuvent donc pas être traduites côté écran : le serveur envoie désormais un
 * CODE à côté de la phrase, et on retombe sur la phrase brute si le code est
 * inconnu — un texte français vaut mieux qu'un écran muet.
 */
import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import { API_BASE as API } from "../../lib/apiBase";
import { useLang } from "../../contexts/LanguageContext";

const RAISONS_EN = {
  no_ranking_items:
    "No gap in this organisation's ranking: the Monday email would not go out. Run “Recompute ranking” first.",
  alerts_disabled:
    "automatic alerts disabled on this pod (staging pod — set SENTINEL_ALERTS_ENABLED=1 to force)",
  test_pod_blocked:
    "sends from the test pod are blocked — “Emails from the test pod” switch in Login Sentinel",
};

export const GapsDigestPreviewModal = ({ open, onClose, canSend = false, onSent }) => {
  const { lang, tr } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  // Le serveur parle français ; en anglais on remplace par le code quand on
  // le connaît, sinon on garde sa phrase plutôt que de n'afficher rien.
  const raison = (code, texte) =>
    (lang === "fr" ? texte : RAISONS_EN[code] || texte) || "";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(
        `${API}/api/videos/coverage-gaps/weekly-digest/render`,
        { withCredentials: true },
      );
      setData(d);
    } catch (e) {
      toast.error(e.response?.data?.detail || tr("Aperçu indisponible", "Preview unavailable"));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [tr]);

  useEffect(() => { if (open) load(); }, [open, load]);

  if (!open) return null;

  const send = async () => {
    if (sending) return;
    setSending(true);
    try {
      const { data: r } = await axios.post(
        `${API}/api/videos/coverage-gaps/weekly-digest/preview`,
        {},
        { withCredentials: true, timeout: 60000 },
      );
      if (r.delivery !== "sent") {
        toast.warning(
          tr(
            `Courriel composé mais NON livré — ${r.blocked_by || r.delivery}.`,
            `Email composed but NOT delivered — ${r.blocked_by || r.delivery}.`,
          ),
          { duration: 14000 },
        );
      } else {
        toast.success(tr(`Envoyé à ${r.to}.`, `Sent to ${r.to}.`), { duration: 8000 });
      }
      onSent?.(r);
    } catch (e) {
      toast.error(
        e.response?.data?.detail || tr("Envoi impossible", "Send failed"),
        { duration: 10000 },
      );
    } finally {
      setSending(false);
    }
  };

  const sujet = data
    ? (lang === "fr" || data.subject_code !== "weekly_gaps_digest"
      ? data.subject
      : "Transcription gaps — top 5 of the week")
    : tr("Chargement…", "Loading…");

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="gaps-digest-render-modal"
    >
      <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[88vh] flex flex-col">
        <div className="flex items-center justify-between gap-3 p-4 border-b border-zinc-200">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-zinc-900 flex items-center gap-2">
              <span aria-hidden="true">👁</span> {tr("Le courriel du lundi", "The Monday email")}
            </h2>
            <p className="text-xs text-zinc-500 truncate">
              {sujet}
              {data?.to ? `${tr(" · destinataire : ", " · recipient: ")}${data.to}` : ""}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-zinc-400 hover:text-zinc-700 text-xl leading-none"
            data-testid="gaps-digest-render-close"
            aria-label="close"
          >×</button>
        </div>

        <div className="p-4 overflow-y-auto flex-1 space-y-3">
          {loading && (
            <p className="py-10 text-center text-sm text-zinc-600" data-testid="gaps-digest-render-loading">
              {tr("Composition de l'aperçu…", "Composing the preview…")}
            </p>
          )}

          {!loading && data && !data.items && (
            <p className="rounded border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900"
               data-testid="gaps-digest-render-empty">
              {raison(data.empty_reason_code, data.empty_reason)}
            </p>
          )}

          {!loading && data?.html && (
            <>
              {/* Le cadre montre le courriel RÉEL, qui part en français : on
                  le dit, pour qu'un lecteur anglophone ne prenne pas ce
                  français pour une traduction oubliée. */}
              {lang !== "fr" && (
                <p className="text-[11px] text-zinc-500" data-testid="gaps-digest-render-lang-note">
                  The email below is shown exactly as it will be sent — it goes out in French.
                </p>
              )}
              <iframe
                title={tr("Aperçu du courriel des écarts", "Gaps email preview")}
                srcDoc={data.html}
                sandbox=""
                className="w-full h-[46vh] min-h-[280px] rounded border border-zinc-200 bg-white"
                data-testid="gaps-digest-render-frame"
              />
            </>
          )}

          {!loading && data && (
            <ul className="text-[11px] text-zinc-600 space-y-1" data-testid="gaps-digest-render-facts">
              <li>
                {data.opt_in
                  ? tr(
                    "Ta préférence est cochée : ce courriel te sera envoyé lundi matin.",
                    "Your preference is ticked: this email will be sent to you on Monday morning.",
                  )
                  : tr(
                    "Ta préférence n'est PAS cochée : tu ne recevras rien lundi (coche la case ci-dessus).",
                    "Your preference is NOT ticked: you will get nothing on Monday (tick the box above).",
                  )}
              </li>
              <li>
                {data.would_send
                  ? tr(
                    "Un envoi depuis cet environnement partirait réellement.",
                    "A send from this environment would actually go out.",
                  )
                  : `${tr("Aucun envoi ne partirait d'ici — ", "Nothing would be sent from here — ")}${
                    raison(data.blocked_by_code, data.blocked_by)
                    || raison(data.empty_reason_code, data.empty_reason)}`}
              </li>
            </ul>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 p-4 border-t border-zinc-200">
          <button
            onClick={load}
            disabled={loading || sending}
            className="px-3 py-2 rounded-lg border border-zinc-300 bg-white hover:bg-zinc-100 text-xs font-semibold disabled:opacity-40"
            data-testid="gaps-digest-render-reload"
          >
            ↻ {tr("Recomposer", "Recompose")}
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-3 py-2 rounded-lg border border-zinc-300 bg-white hover:bg-zinc-100 text-xs font-semibold"
              data-testid="gaps-digest-render-cancel"
            >
              {tr("Fermer sans envoyer", "Close without sending")}
            </button>
            {canSend && (
              <button
                onClick={send}
                disabled={sending || !data?.items}
                className="px-3 py-2 rounded-lg bg-[#002FA7] hover:bg-blue-800 text-white text-xs font-bold disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="gaps-digest-render-send"
              >
                {sending
                  ? `⏳ ${tr("Envoi…", "Sending…")}`
                  : `✉ ${tr("M'envoyer ce courriel", "Send me this email")}`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default GapsDigestPreviewModal;
